import { useState } from "react";
import axios from "axios";
import { Calculator } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EstimateSection = ({ services }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    description: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/estimates`, formData);
      
      toast.success(t.estimate.success);
      
      setFormData({
        name: "",
        phone: "",
        email: "",
        service: "",
        description: ""
      });
    } catch (error) {
      console.error("Error creating estimate:", error);
      toast.error(t.estimate.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Services list for the dropdown
  const serviceOptions = [
    { en: "Emergency Plumbing", es: "Plomería de Emergencia" },
    { en: "Pipe Repair & Replacement", es: "Reparación de Tuberías" },
    { en: "Water Heater Installation", es: "Instalación de Calentadores" },
    { en: "Drain Cleaning", es: "Destapado de Drenajes" },
    { en: "Residential Plumbing", es: "Plomería Residencial" }
  ];

  return (
    <section id="estimado" className="py-10 md:py-14 bg-[#0F172A]" data-testid="estimate-section">
      <div className="px-4 md:px-8 lg:px-16">
        <div className="max-w-lg mx-auto">
          {/* Header - Clean, no extra text */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#D4AF37] rounded-full mb-3">
              <Calculator className="w-6 h-6 text-white" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="estimate-title">
              {language === 'en' ? 'Get a Free Estimate' : 'Obtén un Estimado Gratis'}
            </h2>
          </div>

          {/* Optimized Mobile Form */}
          <form onSubmit={handleSubmit} className="bg-white p-5 md:p-6 rounded-xl space-y-4" data-testid="estimate-form">
            {/* NAME - Required */}
            <div>
              <Label htmlFor="estimate-name" className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                {language === 'en' ? 'Name' : 'Nombre'} *
              </Label>
              <Input
                id="estimate-name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder={language === 'en' ? 'Your full name' : 'Tu nombre completo'}
                className="border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] h-12"
                data-testid="estimate-name-input"
              />
            </div>

            {/* PHONE - Required */}
            <div>
              <Label htmlFor="estimate-phone" className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                {language === 'en' ? 'Phone' : 'Teléfono'} *
              </Label>
              <Input
                id="estimate-phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="(123) 456-7890"
                className="border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] h-12"
                data-testid="estimate-phone-input"
              />
            </div>

            {/* EMAIL - Optional */}
            <div>
              <Label htmlFor="estimate-email" className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                {language === 'en' ? 'Email' : 'Correo'} <span className="text-slate-400 font-normal">({language === 'en' ? 'optional' : 'opcional'})</span>
              </Label>
              <Input
                id="estimate-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="email@example.com"
                className="border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] h-12"
                data-testid="estimate-email-input"
              />
            </div>

            {/* SERVICE - Required */}
            <div>
              <Label htmlFor="estimate-service" className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                {language === 'en' ? 'Service' : 'Servicio'} *
              </Label>
              <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })} required>
                <SelectTrigger className="border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] h-12" data-testid="estimate-service-select">
                  <SelectValue placeholder={language === 'en' ? 'Select a service' : 'Selecciona un servicio'} />
                </SelectTrigger>
                <SelectContent>
                  {serviceOptions.map((service, idx) => (
                    <SelectItem key={idx} value={service.en}>
                      {language === 'en' ? service.en : service.es}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* DESCRIPTION - Optional */}
            <div>
              <Label htmlFor="estimate-description" className="text-xs uppercase tracking-widest text-[#0F172A] mb-1.5 block font-semibold">
                {language === 'en' ? 'Short Description' : 'Descripción Breve'} <span className="text-slate-400 font-normal">({language === 'en' ? 'optional' : 'opcional'})</span>
              </Label>
              <Textarea
                id="estimate-description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder={language === 'en' ? 'Briefly describe your issue...' : 'Describe brevemente tu problema...'}
                className="border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] resize-none"
                data-testid="estimate-description-textarea"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#D4AF37] text-white px-6 py-4 text-sm uppercase tracking-widest font-semibold hover:bg-[#B5952F] transition-all duration-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="estimate-submit-button"
            >
              {isSubmitting 
                ? (language === 'en' ? 'Sending...' : 'Enviando...') 
                : (language === 'en' ? 'Get Free Estimate' : 'Obtener Estimado Gratis')}
            </button>

            {/* Contact Notice */}
            <p className="text-center text-xs text-slate-500 pt-2">
              {language === 'en' 
                ? 'We will contact you by phone or email within 24 hours.' 
                : 'Te contactaremos por teléfono o email dentro de 24 horas.'}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default EstimateSection;